import { useEffect, useState } from 'react';

import { usePDPDynamicContext } from '~/utilities/context/dynamic/PDPDynamicContext';
import { useSizeContext } from '~/utilities/context/dynamic/SizeContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { SizeType } from '~/utilities/graphql/codegen';
import type { StockLevelMessage } from '~/utilities/graphql/codegen';
import { parseStockInformation } from '~/utilities/parsers';

export const useStockLevelMessage = () => {
  const { dynamicProductData } = usePDPDynamicContext();
  const { product } = usePDPContext();
  const { sizeCode, gridValues } = useSizeContext();

  const sizeInformation = dynamicProductData.sizeInformation ?? product.sizeInformation;
  const stockInformation = dynamicProductData.stockInformation ?? product.stockInformation;
  const [stockLevelMessage, setStockLevelMessage] = useState<StockLevelMessage>({
    key: stockInformation?.stockLevel,
    value: stockInformation?.stockMessage,
  });

  const isDoubleSize = sizeInformation?.sizeType === SizeType.NumericDouble;

  useEffect(() => {
    if (sizeInformation?.sizes && sizeCode) {
      if (isDoubleSize) {
        if (gridValues.gridValue1 && gridValues.gridValue2) {
          const stock = parseStockInformation(
            gridValues.gridValue1,
            gridValues.gridValue2,
            stockInformation
          );

          setStockLevelMessage(stock);
        }
      } else {
        const size = sizeInformation?.sizes.find(
          s => s?.code?.toLowerCase() === sizeCode.toLocaleLowerCase()
        );

        if (size) {
          const thresholdsLeft = stockInformation?.stockMapping?.find(
            stockMapping => stockMapping?.key === size.gridValue1
          )?.value?.thresholdsLeft;
          const stockLevel = thresholdsLeft?.[0]?.stockLevel ?? null;
          const stockQuantity = thresholdsLeft?.[0]?.stockQuantity ?? 0;
          const stockMessage = stockInformation?.stockLevelMessages?.find(
            message => message?.key === stockLevel
          )?.value;

          setStockLevelMessage({
            key: stockLevel,
            value: stockMessage?.replace('%s', stockQuantity.toString()),
          });
        }
      }
    } else {
      setStockLevelMessage({
        key: stockInformation?.stockLevel,
        value: stockInformation?.stockMessage,
      });
    }
  }, [gridValues, isDoubleSize, sizeCode, sizeInformation, stockInformation]);

  return {
    stockLevelMessage,
  };
};
